import AuthService from '@/services/AuthService';
import axios from 'axios';

const getInitialState = function () {
  var state = {
    status: {
      loggedIn: false,
    },
    token: null,
    user: null,
    impersonator: null,
    impersonating: false,
    impersonationLoading: false,
    atomicUpdateInProgress: false,
    authenticationVerified: false,
    userWorkInfo: null,
    userResearchCreds: null,
    nextURL: sessionStorage.getItem('nextURL') || '/',
    managedNetworks: null,
    countries: null,
    states: null,
  };

  const user = JSON.parse(localStorage.getItem('user') || null);
  const token = localStorage.getItem('token') || 'false';
  const impersonator = JSON.parse(localStorage.getItem('impersonator') || null);
  const impersonating = localStorage.getItem('impersonating') === 'true';

  if (user) {
    state.status.loggedIn = true;
    state.token = token;
    state.user = user;
    state.impersonator = impersonator;
    state.impersonating = impersonating;
  }

  return state;
};

let service = new AuthService();

export default {
    namespaced: false,
    state: getInitialState(),

    actions: {
        async signIn({ commit, dispatch, rootState }, authCode) {
            //await axios.get('/sanctum/csrf-cookie');

            const brandId = rootState.brand?.currentBrand?.id || null;
            return service.login({ code: authCode, brand_id: brandId }).then(
                data => {
                    commit('token', data.token.token);
                    commit('resetUserInfo', data.user);
                    dispatch('loginOrLogoutDone');
                    return Promise.resolve(data.user);
                },
                error => {
                    commit('loginFailure');
                    return Promise.reject(error);
                }
            );
        },
        // login as another user with a form submission (username/email)
        loginAs({ dispatch, commit }, form) {
            return service.loginAs(form).then(data => {
                commit('token', data.token.token);
                commit('resetUserInfo', data.user);

                dispatch('loginOrLogoutDone');
                return Promise.resolve(data.user);
            });
        },
        logout({ commit, dispatch }) {
            // ensure the user is logged out before we call the API.
            commit('logout');
            return service.logout().then(() => {
                dispatch('loginOrLogoutDone');
                dispatch('clearPersistedStates');
            });
        },

        // called when there is a logout or login event of any kind
        // resets everything and helps the interface "refresh"
        loginOrLogoutDone({ dispatch }) {
            dispatch('network/resetNetworks');
            dispatch('network/resetTrendingNetworks');
        },

        /**
         * This function will make a request to the server to get the user's information, and then it will
         * commit the user's information to the store.
         * Best use of this function is if your component modifies the currently logged in user's profile information.
         * @param context - The context object is the same as the store object.
         */
        reloadUserInfo(context) {
            axios.get('auth/me').then(resp => {
                context.commit('resetUserInfo', resp.data);
            });
        },

        resetUserInfo(context, user) {
            context.commit('resetUserInfo', user);
        },

        resetUserWorkInfo(context, userWorkInfo) {
            context.commit('resetUserWorkInfo', userWorkInfo);
        },

        resetUserResearchCreds(context, userResearchCreds) {
            context.commit('resetUserResearchCreds', userResearchCreds);
        },

        setResettingToken(context) {
            context.commit('setResettingToken');
        },

        // the old token is invalid, so let's refresh the token
        getNewToken(context) {
            return service.refreshToken().then(token => {
                console.log('Update refreshed token to ', token);
                context.commit('token', token);
                return token;
            });
        },

        setNextURL(context, url) {
            context.commit('setNextURL', url);
        },

        // always loads from the networks (no cache)
        loadManagedNetworks(context) {
            return new Promise((resolve, reject) => {
                axios
                    .get('my-managed-networks')
                    .then(resp => {
                        context.commit('setManagedNetworks', resp.data);
                        resolve(resp.data);
                    })
                    .catch(error => reject(error));
            });
        },

        loadCountries(context) {
            return new Promise((resolve, reject) => {
                // always fetch fresh data from the server
                axios
                    .get('countries')
                    .then(resp => {
                        // add a case-normalized field to help with matching
                        const countries = resp.data.map(country => ({
                            ...country,
                            name_lowercase: country.name.toLowerCase(),
                        }));
                        context.commit('setCountries', countries);
                        resolve(countries);
                    })
                    .catch(error => reject(error));
            });
        },

        loadStates(context) {
            return new Promise((resolve, reject) => {
                // always fetch fresh data from the server
                axios
                    .get('states')
                    .then(resp => {
                        context.commit('setStates', resp.data);
                        resolve(resp.data);
                    })
                    .catch(error => reject(error));
            });
        },

        loadStatesByCountry(context, countryId) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`states?country_id=${countryId}`)
                    .then(resp => {
                        let states = context.state.states || [];
                        states = states.filter(state => state.country_id !== countryId);
                        const incoming = new Map(resp.data.map(s => [s.id, s]));
                        states.forEach(s => incoming.set(s.id, s)); // keeps pre-existing states for other countries
                        states = Array.from(incoming.values());
                        context.commit('setStates', states);
                        resolve(states);
                    })
                    .catch(error => reject(error));
            });
        },

        fetchUserWorkInfo({ commit }) {
            return axios
                .get('/profile/workinfo')
                .then(response => {
                    const userWorkInfo = response.data.model;
                    commit('setUserWorkInfo', userWorkInfo);
                })
                .catch(error => {
                    console.error(error);
                    throw error;
                });
        },

        fetchUserResearchCreds({ commit }) {
            return axios
                .get('/profile/researchcreds')
                .then(response => {
                    const userResearchCreds = response.data.model;
                    commit('setUserResearchCreds', userResearchCreds);
                })
                .catch(error => {
                    console.error(error);
                    throw error;
                });
        },

        async impersonate({ dispatch, commit, state }, userIdOrName) {
            // prevent concurrent impersonation operations
            if (state.impersonationLoading) {
                console.warn('Impersonation already in progress');
                return Promise.reject(new Error('Impersonation already in progress'));
            }

            // validate input parameter
            let userId = typeof userIdOrName === 'string' ? userIdOrName.trim() : userIdOrName;
            if (userId === '' || userId === null || userId === undefined) {
                return Promise.reject(new Error('User ID or name is required for impersonation'));
            }

            try {
                //set loading state to prevent concurrent operations
                commit('setImpersonationLoading', true);

                // clear any cached data before starting
                await dispatch('clearAllCaches');

                // Convert string representations of numbers to actual numbers if needed
                if (typeof userId === 'string' && !isNaN(userId) && userId.trim() !== '') {
                    userId = parseInt(userId.trim(), 10);
                }

                // If it's not a number, try to look up the user by username/email
                if (isNaN(userId) || userId === '') {
                    const searchQuery = String(userIdOrName).trim();
                    if (!searchQuery) {
                        throw new Error('Invalid user identifier provided');
                    }

                    console.log('Looking up user by name/email:', searchQuery);
                    const response = await axios.get('users?query=' + encodeURIComponent(searchQuery) + '&limit=1');

                    if (!response.data || response.data.length === 0) {
                        throw new Error(`User not found: ${searchQuery}`);
                    }

                    const foundUser = response.data[0];
                    userId = foundUser.user_id || foundUser.id;

                    if (!userId) {
                        throw new Error('Found user does not have a valid ID');
                    }
                }

                console.log('Impersonating user ID:', userId);

                // perform impersonation
                const response = await axios.post(`impersonate/${userId}`);

                // atomic state update - all or nothing
                commit('beginAtomicUpdate');

                try {
                    // update token
                    commit('token', response.data.token.token);

                    // Update user info
                    commit('resetUserInfo', response.data.user);

                    // update impersonation state
                    if (response.data.impersonator) {
                        commit('setImpersonator', response.data.impersonator);
                        commit('setImpersonating', true);
                    } else {
                        commit('clearImpersonator');
                        commit('setImpersonating', false);
                    }

                    // commit all changes atomically
                    commit('commitAtomicUpdate');
                } catch (error) {
                    // rollback if any step fails
                    commit('rollbackAtomicUpdate');
                    throw error;
                }

                // reset authentication verification flag
                commit('setAuthenticationVerified', false);

                // clear all caches and reset state
                await dispatch('loginOrLogoutDone');

                return Promise.resolve();
            } catch (error) {
                console.error('Impersonation failed:', error);

                // enhanced error handling
                let errorMessage = 'Failed to impersonate user. Please try again.';

                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.data?.errors) {
                    const errors = Object.values(error.response.data.errors).flat();
                    if (errors.length > 0) {
                        errorMessage = errors.join(', ');
                    }
                } else if (error.message) {
                    errorMessage = error.message;
                }

                return Promise.reject(new Error(errorMessage));
            } finally {
                commit('setImpersonationLoading', false);
            }
        },

        // new action to clear all cached data and pending requests
        async clearAllCaches({ dispatch, commit, rootState }) {
            try {
                // clear component-level caches
                if (rootState.notifications !== undefined) {
                    await dispatch('notifications/clearCache', null, { root: true });
                }
                if (rootState.network !== undefined) {
                    await dispatch('network/clearCache', null, { root: true });
                }
                if (rootState.feed !== undefined) {
                    await dispatch('feed/clearCache', null, { root: true });
                }

                // force re-authentication check
                commit('setAuthenticationVerified', false);
            } catch (error) {
                console.warn('Error clearing caches:', error);
            }
        },

        exitImpersonate({ dispatch, commit, state }) {
            // if there's no impersonator, we were not logged in when impersonation started
            if (!state.impersonator) {
                console.log('No impersonator found - exiting to anonymous state');
                // call the backend anyway to ensure proper session cleanup
                return axios
                    .post('impersonate-exit', {})
                    .then(() => {
                        // just perform a regular logout
                        commit('logout');
                        dispatch('loginOrLogoutDone');
                        return Promise.resolve();
                    })
                    .catch(error => {
                        console.error('Error during logout:', error);
                        // still perform the frontend logout
                        commit('logout');
                        dispatch('loginOrLogoutDone');
                        return Promise.resolve();
                    });
            }
            return axios
                .post('impersonate-exit', {
                    impersonator_id: state.impersonator.id || state.impersonator.user_id,
                })
                .then(response => {
                    // store the new token
                    commit('token', response.data.token.token);
                    // restore the original user
                    commit('resetUserInfo', response.data.user);
                    // clear the impersonator
                    commit('clearImpersonator');
                    // set the impersonating flag
                    commit('setImpersonating', false);
                    dispatch('loginOrLogoutDone');
                    return Promise.resolve();
                })
                .catch(error => {
                    console.error('Error exiting impersonation:', error);
                    // even if there's an error, try to reset the state
                    commit('clearImpersonator');
                    commit('setImpersonating', false);
                    return Promise.reject(error);
                });
        },
    },
    getters: {
        // returns true if a user is logged in
        auth: state => {
            return state.status.loggedIn;
        },
        user: state => {
            return state.user;
        },
        userWorkInfo: state => {
            return state.userWorkInfo;
        },
        userResearchCreds: state => {
            return state.userResearchCreds;
        },
        zoomUser: state => {
            return state.user.zoom_user;
        },
        isUserIntegratedToZoom: state => {
            var linked = false;
            if (state.user.zoom_user && state.user.zoom_user.zoom_user_id) linked = true;

            return linked;
        },
        token: state => {
            return state.token;
        },
        getNextURL: state => {
            return state.nextURL;
        },
        managedNetworks: state => {
            return state.managedNetworks;
        },
        countries: state => {
            return state.countries;
        },
        states: state => {
            return state.states;
        },
        isImpersonating: state => {
            return state.impersonating;
        },
        impersonator: state => {
            return state.impersonator;
        },
    },
    mutations: {
        token(state, newToken) {
            localStorage.setItem('token', newToken);

            state.token = newToken;
            state.status.loggedIn = newToken != null;

            axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
        },

        loginFailure(state) {
            state.status.loggedIn = false;
            state.user = null;
        },
        logout(state) {
            state.status.loggedIn = false;
            state.user = null;
            state.impersonator = null;
            state.impersonating = false;
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('impersonator');
            localStorage.removeItem('impersonating');

            // remove the header
            delete axios.defaults.headers.common['Authorization'];
        },
        resetUserInfo(state, user) {
            state.user = user;
            localStorage.setItem('user', JSON.stringify(state.user));
        },
        resetUserWorkInfo(state, userWorkInfo) {
            state.userWorkInfo = userWorkInfo;
            localStorage.setItem('userWorkInfo', JSON.stringify(state.userWorkInfo));
        },
        resetUserResearchCreds(state, userResearchCreds) {
            state.userResearchCreds = userResearchCreds;
            localStorage.setItem('userResearchCreds', JSON.stringify(state.userResearchCreds));
        },

        setImpersonator(state, impersonator) {
            state.impersonator = impersonator;
            localStorage.setItem('impersonator', JSON.stringify(impersonator));
        },

        clearImpersonator(state) {
            state.impersonator = null;
            localStorage.removeItem('impersonator');
        },

        setNextURL(state, url) {
            sessionStorage.setItem('nextURL', url);
            state.nextURL = url;
        },

        setManagedNetworks(state, networks) {
            state.managedNetworks = networks;
        },

        setCountries(state, countries) {
            state.countries = countries;
        },

        setStates(state, states) {
            state.states = states;
        },

        setUserWorkInfo(state, userWorkInfo) {
            state.userWorkInfo = userWorkInfo;
        },

        setUserResearchCreds(state, userResearchCreds) {
            state.userResearchCreds = userResearchCreds;
        },

        REMOVE_NETWORK(state, index) {
            state.managedNetworks.splice(index, 1);
        },

        RESET_STATE(state) {
            Object.assign(state, getInitialState());
        },

        setImpersonating(state, status) {
            state.impersonating = status;
            localStorage.setItem('impersonating', status);
        },

        setImpersonationLoading(state, loading) {
            state.impersonationLoading = loading;
        },

        setAuthenticationVerified(state, verified) {
            state.authenticationVerified = verified;
        },

        // atomic update mutations
        beginAtomicUpdate(state) {
            state.atomicUpdateInProgress = true;
            // store current state for potential rollback
            state._rollbackState = {
                token: state.token,
                user: { ...state.user },
                impersonator: state.impersonator ? { ...state.impersonator } : null,
                impersonating: state.impersonating,
                status: { ...state.status },
            };
        },

        commitAtomicUpdate(state) {
            state.atomicUpdateInProgress = false;
            delete state._rollbackState;
        },

        rollbackAtomicUpdate(state) {
            if (state._rollbackState) {
                state.token = state._rollbackState.token;
                state.user = state._rollbackState.user;
                state.impersonator = state._rollbackState.impersonator;
                state.impersonating = state._rollbackState.impersonating;
                state.status = state._rollbackState.status;

                // update localStorage to match rolled back state
                if (state.token) {
                    localStorage.setItem('token', state.token);
                    axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
                } else {
                    localStorage.removeItem('token');
                    delete axios.defaults.headers.common['Authorization'];
                }

                if (state.user) {
                    localStorage.setItem('user', JSON.stringify(state.user));
                } else {
                    localStorage.removeItem('user');
                }

                if (state.impersonator) {
                    localStorage.setItem('impersonator', JSON.stringify(state.impersonator));
                } else {
                    localStorage.removeItem('impersonator');
                }

                localStorage.setItem('impersonating', state.impersonating);

                delete state._rollbackState;
            }
            state.atomicUpdateInProgress = false;
        },
    },
};
